import * as Sentry from '@sentry/react';

import Logger from './Logger';
import { applySentryRuntimeContext } from '../../utils/sentryContext';

class SentryLogger extends Logger {
  static instance = null;

  constructor() {
    super();
    if (!SentryLogger.instance) {
      SentryLogger.instance = this;
    }
    return SentryLogger.instance;
  }

  // Kept so existing callers do not break. Sentry is initialized in index.js and logging is always on.
  setEnabled(_state) {}

  info(message, options = {}) {
    this.captureMessage(message, 'info', options);
  }

  debug(message, options = {}) {
    this.captureMessage(message, 'debug', options);
  }

  error(error, options = {}) {
    const exception = this.toError(error);

    try {
      Sentry.withScope((scope) => {
        applySentryRuntimeContext(scope);
        this.applyOptions(scope, options);
        scope.setLevel('error');
        Sentry.captureException(exception);
      });
    } catch (sentryError) {
      console.error('Failed to report error to Sentry', sentryError);
    }

    console.error(error, options);
  }

  captureMessage(message, level = 'info', options = {}) {
    try {
      Sentry.withScope((scope) => {
        applySentryRuntimeContext(scope);
        this.applyOptions(scope, options);
        scope.setLevel(level);
        Sentry.captureMessage(typeof message === 'string' ? message : this.stringify(message));
      });
    } catch (sentryError) {
      console.error('Failed to report message to Sentry', sentryError);
    }
  }

  applyOptions(scope, options = {}) {
    if (options.extra) {
      Object.entries(options.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    if (options.tags) {
      Object.entries(options.tags).forEach(([key, value]) => {
        if (value != null) {
          scope.setTag(key, String(value));
        }
      });
    }
  }

  toError(error) {
    if (error instanceof Error) {
      return error;
    }
    if (typeof error === 'string') {
      return new Error(error);
    }
    const wrapped = new Error(this.stringify(error));
    wrapped.cause = error;
    return wrapped;
  }

  stringify(value) {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
}

export default SentryLogger;
